import React, { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';

// Typing in the search box re-renders every column, so the store write (and
// with it the filtering pass over every task) trails the keystrokes.
export const SEARCH_DEBOUNCE_MS = 180;

/** Focus is somewhere the "/" key types a character. */
function isTypingTarget(el) {
  if (!el || el === document.body) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return !!el.isContentEditable || !!el.closest?.('[contenteditable="true"]');
}

/**
 * The in-board task search (card 4.2): a 240 × 32 box with a "/" key hint,
 * the shortcut GitHub, Linear and YouTube use to jump to search.
 *
 * - "/" focuses it when nothing editable has focus and the board is the
 *   visible view (`hotkeyActive`) — the board is kept mounted while hidden, and
 *   a hidden box must not swallow the key.
 * - Escape in the box clears it and gives focus back to the board, like
 *   Linear's search field.
 * - Same fixed size typed or empty: the hint and the ✕ share one slot, so the
 *   box never grows when you start typing (owner, 01.10.2026).
 */
export default function BoardFilterSearch({ value = '', onCommit, hotkeyActive = true }) {
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);

  // Keep the box in step when the filters are cleared from elsewhere ("Clear",
  // or opening a different board).
  useEffect(() => {
    setDraft((d) => (d === value ? d : value));
  }, [value]);

  useEffect(() => {
    if (draft === value) return undefined;
    const t = setTimeout(() => onCommit(draft), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- value is read only to skip a redundant write; depending on it would restart the timer on our own commit
  }, [draft, onCommit]);

  useEffect(() => {
    if (!hotkeyActive) return undefined;
    const onKey = (e) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) return;
      if (isTypingTarget(document.activeElement)) return;
      const input = inputRef.current;
      if (!input) return;
      // A modal over the board (task details, Edit board) owns the keyboard.
      // (ui/Modal and TaskDetailModal both render a .modal-backdrop.)
      if (document.querySelector('.modal-backdrop, [role="dialog"]')) return;
      if (typeof input.checkVisibility === 'function' && !input.checkVisibility()) return;
      e.preventDefault();
      input.focus();
      input.select();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [hotkeyActive]);

  const clear = () => {
    setDraft('');
    // Commit at once: a clear is not typing, nothing to debounce.
    onCommit('');
  };

  return (
    <label className="task-board-filter-search task-board-filter-search--board">
      <Search size={14} aria-hidden />
      <input
        ref={inputRef}
        type="text"
        value={draft}
        placeholder="Search tasks…"
        aria-label="Search tasks"
        aria-keyshortcuts="/"
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Escape') return;
          e.preventDefault();
          e.stopPropagation();
          if (draft) clear();
          e.currentTarget.blur();
        }}
      />
      {draft ? (
        <button type="button" aria-label="Clear search" onMouseDown={(e) => e.preventDefault()} onClick={clear}>
          <X size={12} />
        </button>
      ) : (
        <kbd className="board-filter-kbd" aria-hidden>
          /
        </kbd>
      )}
    </label>
  );
}
